/**
 * UserGroups.js
 * Component for displaying user's groups with academic year filter
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Badge,
  Alert,
  Spinner,
  Button,
  Form
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { userService, commonService } from '../../services/api';
import UserAttendanceModal from './UserAttendanceModal';
import EditSubscriptionModal from '../Subscriptions/EditSubscriptionModal';
import { getAcademicYearOptions } from '../../helpers/timeHelper';
import { isPayPerUseOffer, isMonthlyOffer } from '../../helpers/subscriptionHelpers';

const UserGroups = ({ userId, user }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  
  // Modal state
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Edit subscription modal state
  const [showEditSubscriptionModal, setShowEditSubscriptionModal] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);

  // Load current academic year
  const loadCurrentAcademicYear = useCallback(async () => {
    try {
      const response = await commonService.getCurrentAcademicYear();
      
      if (response.success && response.data !== undefined && response.data !== null) {
        const currentYear = response.data; // La risposta è direttamente l'anno accademico
        
        setSelectedYear(currentYear.toString());
        
        // Generate academic year options
        const years = getAcademicYearOptions(currentYear).map(year => {
          return {
            value: year,
            label: `${year}/${year + 1}`
          };
        });
        setAcademicYears(years);
      } else {
        console.error('Invalid response from getCurrentAcademicYear:', response);
        setError('Errore nel caricamento dell\'anno sportivo');
      }
    } catch (err) {
      console.error('Error loading current academic year:', err);
      setError('Errore nel caricamento dell\'anno sportivo');
    }
  }, []);

  // Load user groups
  const loadUserGroups = useCallback(async () => {
    if (!userId || !selectedYear) return;

    try {
      setLoading(true);
      setError('');
      
      const response = await userService.getUserGroups(userId, parseInt(selectedYear), includeArchived, true);
      
      if (response.success) {
        setGroups(response.data);
      } else {
        setError(response.message || 'Errore nel caricamento dei gruppi');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error loading user groups:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, selectedYear, includeArchived]);

  // Handle year change
  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  // Show attendance modal
  const showAttendance = (group) => {
    setSelectedGroup(group);
    setShowAttendanceModal(true);
  };

  // Show edit subscription modal
  const handleEditSubscription = (subscription, group) => {
    setEditingSubscription(subscription);
    setEditingGroup(group);
    setShowEditSubscriptionModal(true);
  };

  // Create course link with filters
  const createCourseLink = (course) => {
    const params = new URLSearchParams();
    if (course?.name) {
      params.append('search', course.name);
    }
    if (course?.academicYear) {
      params.append('academicYear', course.academicYear);
    }
    return `/courses?${params.toString()}`;
  };

  // Effects
  useEffect(() => {
    loadCurrentAcademicYear();
  }, [loadCurrentAcademicYear]);

  useEffect(() => {
    if (selectedYear) {
      loadUserGroups();
    }
  }, [loadUserGroups, selectedYear]);

  if (loading && !groups.length) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Caricamento gruppi...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="user-groups">
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {/* Filter Controls */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Anno Sportivo</Form.Label>
                <Form.Select
                  value={selectedYear}
                  onChange={handleYearChange}
                  disabled={loading}
                >
                  <option value="">Seleziona anno...</option>
                  {academicYears.map(year => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <Form.Check
                type="switch"
                id="include-archived-switch"
                label="Mostra archiviati"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                disabled={loading || !selectedYear}
                className="mb-2"
              />
            </Col>
            <Col md={4} className="d-flex align-items-end">
              {groups.length > 0 && (
                <div className="ms-3">
                  <Badge bg="info">
                    Gruppi trovati: {groups.length}
                  </Badge>
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Groups List */}
      <Card>
        <Card.Body>
          <h5 className="mb-3">Gruppi dell'Utente</h5>
          
          {!selectedYear ? (
            <div className="text-center py-4">
              <i className="fas fa-filter fa-3x text-muted mb-3"></i>
              <p className="text-muted">Seleziona un anno sportivo per visualizzare i gruppi</p>
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-4">
              <i className="fas fa-users fa-3x text-muted mb-3"></i>
              <p className="text-muted">
                Nessun gruppo trovato per l'anno sportivo {selectedYear}/{parseInt(selectedYear) + 1}
              </p>
            </div>
          ) : (
            <Table responsive striped>
              <thead>
                <tr>
                  <th>Nome Gruppo</th>
                  <th>Corso</th>
                  <th>Anno Sportivo</th>
                  <th>Abbonamento</th>
                  <th className="text-center">Inizio</th>
                  <th className="text-center">Fine</th>
                  <th className="text-center">Prezzo</th>
                  <th className="text-center">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => {
                  // Trova la subscription attiva per questo gruppo
                  const activeSubscription = group.courseSubscriptions?.[0];
                  
                  const isArchived = activeSubscription?.isArchived === true;
                  
                  return (
                    <tr key={group.id} className={isArchived ? 'table-secondary' : ''}>
                      <td>
                        <Link 
                          to={`/groups/${group.id}/participants`}
                          className="text-decoration-none"
                          title={`Visualizza partecipanti del gruppo ${group.name}`}
                        >
                          <strong>{group.name}</strong>
                        </Link>
                        {isArchived && (
                          <Badge bg="secondary" className="ms-2" pill>Archiviato</Badge>
                        )}
                        {group.description && (
                          <div className="text-muted small">{group.description}</div>
                        )}
                      </td>
                      <td>
                        {group.course?.name ? (
                          <Link 
                            to={createCourseLink(group.course)}
                            className="text-decoration-none"
                            title={`Visualizza corso ${group.course.name}`}
                          >
                            {group.course.name}
                          </Link>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <Badge bg="primary">
                          {group.course?.academicYear}/{group.course?.academicYear + 1}
                        </Badge>
                      </td>
                      <td>
                        {activeSubscription?.offer ? (
                          <div
                            role="button"
                            style={{ cursor: 'pointer' }}
                            title="Clicca per modificare l'iscrizione"
                            onClick={() => handleEditSubscription(activeSubscription, group)}
                          >
                            <span className="fw-medium text-primary">{activeSubscription.offer.name}</span>
                            {isPayPerUseOffer(activeSubscription.offer) && (
                              <small className="text-muted d-block">Pay-per-use</small>
                            )}
                            {isMonthlyOffer(activeSubscription.offer) && (
                              <small className="text-muted d-block">
                                {activeSubscription.offer.durationMonths || 0} mes{activeSubscription.offer.durationMonths === 1 ? 'e' : 'i'}
                              </small>
                            )}
                            {!isPayPerUseOffer(activeSubscription.offer) && !isMonthlyOffer(activeSubscription.offer) && (
                              <small className="text-info d-block">Data Libera</small>
                            )}
                            {activeSubscription.hasInstallments && (
                              <small className="text-info d-block">
                                <i className="fas fa-coins me-1"></i>
                                A rate ({activeSubscription.installments?.filter(i => i.paymentDate).length || 0}/{activeSubscription.installments?.length || 0})
                              </small>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted fst-italic">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        <small className="text-muted">
                          {activeSubscription?.startDate 
                            ? new Date(activeSubscription.startDate).toLocaleDateString('it-IT') 
                            : '-'}
                        </small>
                      </td>
                      <td className="text-center">
                        <small className={
                          activeSubscription?.endDate && new Date(activeSubscription.endDate) < new Date()
                            ? "text-danger fw-bold"
                            : "text-muted"
                        }>
                          {activeSubscription?.endDate 
                            ? new Date(activeSubscription.endDate).toLocaleDateString('it-IT') 
                            : '-'}
                        </small>
                      </td>
                      <td className="text-center">
                        <span className="fw-bold text-success">
                          {activeSubscription?.price != null 
                            ? `€${activeSubscription.price.toFixed(2)}` 
                            : '-'}
                        </span>
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => showAttendance(group)}
                          title="Visualizza presenze"
                        >
                          <i className="fas fa-calendar-check me-1"></i>
                          Presenze
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
          
          {loading && groups.length > 0 && (
            <div className="text-center py-2">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Aggiornamento...</span>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Attendance Modal */}
      {selectedGroup && (
        <UserAttendanceModal
          show={showAttendanceModal}
          onHide={() => {
            setShowAttendanceModal(false);
            setSelectedGroup(null);
          }}
          userId={userId}
          group={selectedGroup}
        />
      )}

      {/* Edit Subscription Modal */}
      <EditSubscriptionModal
        show={showEditSubscriptionModal}
        onHide={() => {
          setShowEditSubscriptionModal(false);
          setEditingSubscription(null);
          setEditingGroup(null);
        }}
        subscription={editingSubscription}
        user={user}
        groupId={editingGroup?.id}
        courseId={editingGroup?.course?.id}
        onSuccess={loadUserGroups}
      />
    </div>
  );
};

export default UserGroups;